import StateCodeMapping from "@/utils/google-maps-state-code-mapping"
class AddressExtractor {

    static get NEEDED_FIELDS() {
        return [
            "street_number",
            "route",
            "sublocality_level_2",
            "sublocality_level_1",
            "locality",
            "administrative_area_level_1",
            "country",
            "postal_code",
            "postal_town",
            "subpremise",
            "name"
        ]
    }

    static get ALIASES() {
        return {
            "billing_addr1": "line1",
            "billing_addr2": "line2",
            "billing_addr3": "line3",
            "billing_state": "state",
            "billing_region": "region",
            "billing_state_code": "state_code",
            "billing_zip": "zip",
            "billing_city": "city",
            "billing_country": "country"
        }
    }

    static extractAddress(value) {
        const addressComponents = value.addressComponents;
        const enabledFields = value.enabledFields;
        const formattedAddress = value.formattedAddress;
        const extracted = this._extract(addressComponents);
        let output = {};
        output["line1"] = this._constructLine1(extracted, formattedAddress);
        output["line2"] = this._constructLine2(extracted, formattedAddress);
        logger.kvl({
            "hp_addr_line1_extracted_name": extracted.name.long_name,
            "hp_addr_line1_return_logic": output['line1']
        });
        output["line3"] = "";
        output["zip"] = this._test(extracted['postal_code']);
        output["country"] = extracted['country'] && extracted['country'].short_name;
        if (["GB", "SE"].indexOf(output["country"]) > -1) {
            output["city"] = this._test(extracted['postal_town']);
        } else {
            output["city"] = this._test(extracted['locality']);
        }
        let isCountryEnabled = enabledFields &&
            ((enabledFields.indexOf("country") > -1) || enabledFields.indexOf("billing_country") > -1);
        if (isCountryEnabled && ["US", "CA", "IN", "BR","AE"].indexOf(output["country"]) > -1) {
            let state = extracted['administrative_area_level_1'];
            let country = output["country"];
            if (state && state.short_name) {
                let stateCode = state.short_name;
                let value = StateCodeMapping[country] &&
                    StateCodeMapping[country][stateCode];
                output["state_code"] = value ? value : stateCode;
            }
        } else {
            output["state"] = this._test(extracted['administrative_area_level_1']);
        }
        if (isCountryEnabled && ["US", "CA", "IN","AE"].indexOf(output["country"])) {
            output["region"] = output["state_code"];
        } else {
            output["region"] = output["state"];
        }
        return output;
    }

    static _extract(addressComponents) {
        return addressComponents.reduce((map, value) => {
            var matchedType = value.types.filter((v) => this.NEEDED_FIELDS.indexOf(v) != -1)[0]
            if (matchedType && !map[matchedType]) {
                map[matchedType] = value;
            }
            return map;
        }, {})
    }

    static _constructLine1(extracted, formattedAddress) {
        const streetNumber = this._test(extracted["street_number"]);
        const route = this._test(extracted["route"]);
        const subpremise = this._test(extracted["subpremise"]);

        // If formatted_address is available and we have both street_number and route, use it to determine order
        if (formattedAddress && streetNumber && route) {
            // Check for both short and long forms in formatted_address
            const streetNumberLong = extracted["street_number"]?.long_name;
            const streetNumberShort = extracted["street_number"]?.short_name;
            const routeLong = extracted["route"]?.long_name;
            const routeShort = extracted["route"]?.short_name;

            // Find the actual text used in formatted_address for each component
            let streetNumberInFormatted = null;
            let routeInFormatted = null;

            // Check which form of street number appears in formatted_address
            if (streetNumberLong && formattedAddress.indexOf(streetNumberLong) !== -1) {
                streetNumberInFormatted = streetNumberLong;
            } else if (streetNumberShort && formattedAddress.indexOf(streetNumberShort) !== -1) {
                streetNumberInFormatted = streetNumberShort;
            }

            // Check which form of route appears in formatted_address
            if (routeLong && formattedAddress.indexOf(routeLong) !== -1) {
                routeInFormatted = routeLong;
            } else if (routeShort && formattedAddress.indexOf(routeShort) !== -1) {
                routeInFormatted = routeShort;
            }

            // If we found both components in formatted_address, determine order
            if (streetNumberInFormatted && routeInFormatted) {
                const streetNumberIndex = formattedAddress.indexOf(streetNumberInFormatted);
                const routeIndex = formattedAddress.indexOf(routeInFormatted);

                // Determine order based on positions in formatted_address
                if (streetNumberIndex < routeIndex) {
                    // Street number comes before route (e.g., "123 Main St")
                    if (subpremise) {
                        return `${subpremise} / ${streetNumber} ${route}`.trim();
                    } else {
                        return `${streetNumber} ${route}`.trim();
                    }
                } else {
                    // Route comes before street number (e.g., "Main St 123")
                    if (subpremise) {
                        return `${route} ${subpremise} / ${streetNumber}`.trim();
                    } else {
                        return `${route} ${streetNumber}`.trim();
                    }
                }
            }
        }

        // Fallback to existing behavior
        var line1 = [];
        if(streetNumber){
            line1.push(streetNumber)
            if(subpremise)
                line1.unshift(subpremise,'/')
        }
        if(extracted['country'] && ['DE', 'NL'].indexOf(extracted['country'].short_name) > -1){
            line1.unshift(route)
        }
        else{
            line1.push(route)
        }
        return line1.join(" ").trim();
    }

    static _constructLine2(extracted, formattedAddress) {
        const sublocalityLevel2 = this._test(extracted["sublocality_level_2"]);
        const sublocalityLevel1 = this._test(extracted["sublocality_level_1"]);

        // If formatted_address is available and we have both components, determine order based on it
        if (formattedAddress && sublocalityLevel2 && sublocalityLevel1) {
            // Check for both short and long forms in formatted_address
            const level2Long = extracted["sublocality_level_2"]?.long_name;
            const level2Short = extracted["sublocality_level_2"]?.short_name;
            const level1Long = extracted["sublocality_level_1"]?.long_name;
            const level1Short = extracted["sublocality_level_1"]?.short_name;

            // Find the actual text used in formatted_address for each component
            let level2InFormatted = null;
            let level1InFormatted = null;

            // Check which form of sublocality_level_2 appears in formatted_address
            if (level2Long && formattedAddress.indexOf(level2Long) !== -1) {
                level2InFormatted = level2Long;
            } else if (level2Short && formattedAddress.indexOf(level2Short) !== -1) {
                level2InFormatted = level2Short;
            }

            // Check which form of sublocality_level_1 appears in formatted_address
            if (level1Long && formattedAddress.indexOf(level1Long) !== -1) {
                level1InFormatted = level1Long;
            } else if (level1Short && formattedAddress.indexOf(level1Short) !== -1) {
                level1InFormatted = level1Short;
            }

            // If both are found in the formatted address, use their order
            if (level2InFormatted && level1InFormatted) {
                const level2Index = formattedAddress.indexOf(level2InFormatted);
                const level1Index = formattedAddress.indexOf(level1InFormatted);

                if (level2Index < level1Index) {
                    return `${sublocalityLevel2}, ${sublocalityLevel1}`;
                } else {
                    return `${sublocalityLevel1}, ${sublocalityLevel2}`;
                }
            }
        }

        // Fallback to existing behavior
        return [sublocalityLevel2, sublocalityLevel1].filter(v => !!v).join(", ");
    }

    static _test(obj) {
        return obj && obj.long_name;
    }
}

export default AddressExtractor
